import { useState, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { Box, Typography, Button } from "@mui/material";
import { Pencil, ThumbsUp } from "lucide-mui";
import { useTheme } from "../context/ThemeContext";
import { EditProcedureModal, OptionsPromptButtons } from "../components";

const mdTextSx = isDark => ({
  fontSize: "14px",
  lineHeight: "20px",
  color: isDark ? "#D5D9DF" : "#434950",
  fontFamily: "Inter",
  fontWeight: "400",
  letterSpacing: "0.25%",
  mb: 1,
});

const statusMessageSx = isDark => ({
  color: isDark ? "#D5D9DF" : "#434950",
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "28px",
  padding: "12px 20px",
  background: isDark ? "#1E2022" : "#FFFFFF",
  mt: "8px",
  border: "1px solid",
  borderColor: isDark ? "#313131" : "#D5D9DF",
  borderRadius: "12px",
});

export default function AgentMessageComponent({
  message,
  onApproveSkeleton,
  onUpdateSkeleton,
  onViewDpm,
  currentAnchorId,
  onEditingChange,
  onToggleSidebar,
  onSelectOption,
  isLoading,
}) {
  const { isDark } = useTheme();
  const [isProcedureModalOpen, setIsProcedureModalOpen] = useState(false);

  useEffect(() => {
    onEditingChange?.(isProcedureModalOpen);
  }, [isProcedureModalOpen, onEditingChange]);
  const { value } = message;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        mt: 1.5,
        mr: "24px",
        maxWidth: "calc(100% - 24px)",
      }}
    >
      <Box sx={{ flex: 1, maxWidth: "100%" }}>
        {value?.topic === "CLARIFICATION_NEEDED" &&
        value?.clarification_message ? (
          <Typography sx={mdTextSx(isDark)}>
            {value.clarification_message}
          </Typography>
        ) : value?.topic === "OPTIONS_PROMPT" ? (
          <Box>
            <Typography sx={mdTextSx(isDark)}>{value.message}</Typography>
            {!value.answered && (
              <OptionsPromptButtons
                options={value.options}
                disabled={isLoading}
                onSelect={option => onSelectOption?.(option, value)}
              />
            )}
          </Box>
        ) : value?.topic === "SKELETON_GENERATED" && value?.data ? (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "20px",
                color: isDark ? "#A8AEB6" : "#434950",
                mb: "8px",
                letterSpacing: "0%",
              }}
            >
              Procedure Outline
            </Typography>
            <Box
              sx={{
                borderRadius: "12px",
                border: "1px solid",
                borderColor: isDark ? "#313131" : "#D5D9DF",
              }}
            >
              {(() => {
                const skeletonData = value?.data?.plan;
                if (!skeletonData) return null;
                const { title, phases } = skeletonData;
                return (
                  <>
                    {title && (
                      <Box
                        sx={{
                          padding: "16px",
                          borderTopLeftRadius: "12px",
                          borderTopRightRadius: "12px",
                          background: isDark ? "#383D43" : "#ECEFF3",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "16px",
                            lineHeight: "20px",
                            color: isDark ? "#ECEFF3" : "#434950",
                            fontFamily: "Inter",
                            fontWeight: 400,
                            letterSpacing: "0%",
                          }}
                        >
                          {title}
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        padding: "12px 20px",
                        background: isDark ? "#1E2022" : "#FFFFFF",
                        borderBottomLeftRadius:
                          value?.status === "waiting_for_user_action"
                            ? 0
                            : "12px",
                        borderBottomRightRadius:
                          value?.status === "waiting_for_user_action"
                            ? 0
                            : "12px",
                      }}
                    >
                      {Array.isArray(phases) &&
                        phases.map((phase, index) => (
                          <Box
                            key={index}
                            sx={{ mb: "32px", "&:last-child": { mb: 0 } }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "Inter",
                                fontWeight: 500,
                                fontSize: "16px",
                                lineHeight: "20px",
                                color: isDark ? "#D5D9DF" : "#434950",
                                letterSpacing: "0%",
                              }}
                            >
                              {phase.name}
                            </Typography>

                            {phase.phase_summary && (
                              <Typography
                                sx={{
                                  fontFamily: "Inter",
                                  fontWeight: 400,
                                  fontSize: "14px",
                                  lineHeight: "20px",
                                  color: isDark ? "#D5D9DF" : "#434950",
                                  mt: "16px",
                                  letterSpacing: "0.25%",
                                }}
                              >
                                {phase.phase_summary}
                              </Typography>
                            )}

                            {Array.isArray(phase.phase_outline) &&
                              phase.phase_outline.length > 0 && (
                                <Box
                                  component='ul'
                                  sx={{ ml: "18px", listStyleType: "disc" }}
                                >
                                  {phase.phase_outline.map(
                                    (item, itemIndex) => (
                                      <Typography
                                        component='li'
                                        key={itemIndex}
                                        sx={{
                                          fontFamily: "Inter",
                                          fontWeight: 400,
                                          fontSize: "14px",
                                          lineHeight: "20px",
                                          color: isDark ? "#D5D9DF" : "#434950",
                                          margin: "10px 0",
                                          alignSelf: "stretch",
                                        }}
                                      >
                                        {item}
                                      </Typography>
                                    ),
                                  )}
                                </Box>
                              )}
                          </Box>
                        ))}
                    </Box>

                    {value?.status === "waiting_for_user_action" && (
                      <>
                        <EditProcedureModal
                          initialSkeleton={value?.data?.plan}
                          isProcedureModalOpen={isProcedureModalOpen}
                          setIsProcedureModalOpen={setIsProcedureModalOpen}
                          currentAnchorId={currentAnchorId}
                          onCancel={() => setIsProcedureModalOpen(false)}
                          onSave={newSkeleton => {
                            setIsProcedureModalOpen(false);
                            onUpdateSkeleton?.(
                              value.request_id,
                              newSkeleton,
                              value?.data?.dpm_uuid,
                            );
                          }}
                          onToggleSidebar={onToggleSidebar}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            background: isDark ? "#383D43" : "#ECEFF3",
                            p: "20px 12px",
                            borderBottomLeftRadius: "12px",
                            borderBottomRightRadius: "12px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                            }}
                          >
                            <Button
                              onClick={() => setIsProcedureModalOpen(true)}
                              disabled={
                                isProcedureModalOpen ||
                                value?.status === "approving"
                              }
                              sx={{
                                borderRadius: "12px",
                                background: isDark
                                  ? "linear-gradient(135deg, rgba(168, 174, 182, 0.5) 0%, rgba(126, 132, 141, 0.5) 100%)"
                                  : "linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(226, 231, 235, 0.9) 50%, #D3DAE1 100%)",
                                height: "36px",
                                width: "fit-content",
                                padding: "8px 12px",
                                color: isDark ? "#ffffff" : "#434950",
                                display: "flex",
                                alignItems: "center",
                                textTransform: "none",
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: "500",
                                lineHeight: "0.15%",
                                letterSpacing: "0.07px",
                                border: "1px solid",
                                borderColor: isDark
                                  ? "rgba(168,174,182,0.5)"
                                  : "#D5D9DF",
                                "&:hover": {
                                  background: isDark
                                    ? "linear-gradient(135deg, rgba(168, 174, 182, 0.5) 0%, rgba(126, 132, 141, 0.5) 100%)"
                                    : "linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(226, 231, 235, 0.9) 50%, #D3DAE1 100%)",
                                },
                                "&.Mui-disabled": {
                                  cursor: "not-allowed",
                                  pointerEvents: "auto",
                                  color: isDark ? "#D5D9DF" : "#434950",
                                  opacity: 0.6,
                                },
                              }}
                            >
                              <Pencil sx={{ height: "12px", mr: "2px" }} />
                              Edit
                            </Button>

                            <Button
                              onClick={() =>
                                onApproveSkeleton?.(
                                  value?.request_id,
                                  value?.data?.dpm_uuid,
                                )
                              }
                              disabled={
                                isProcedureModalOpen ||
                                value?.status === "approving"
                              }
                              sx={{
                                borderRadius: "12px",
                                background:
                                  "linear-gradient(135deg, #146E37 0%, #1A8744 100%)",
                                height: "36px",
                                width: "fit-content",
                                padding: "8px 12px",
                                color: "#FFFFFF",
                                display: "flex",
                                alignItems: "center",
                                textTransform: "none",
                                fontFamily: "Inter",
                                fontSize: "14px",
                                fontWeight: "500",
                                lineHeight: "20px",
                                letterSpacing: "0.15%",
                                "&:hover": {
                                  background:
                                    "linear-gradient(135deg, #146E37 0%, #1A8744 100%)",
                                },
                                "&.Mui-disabled": {
                                  cursor: "not-allowed",
                                  pointerEvents: "auto",
                                  color: "#FFFFFF",
                                  opacity: 0.6,
                                },
                              }}
                            >
                              <ThumbsUp sx={{ height: "16px", mr: "2px" }} />
                              Approve
                            </Button>
                          </Box>

                          <Typography
                            sx={{
                              mt: "10px",
                              color: isDark ? "#A8AEB6" : "#434950",
                              fontFamily: "Inter",
                              fontSize: "14px",
                              fontWeight: 400,
                              lineHeight: "20px",
                              letterSpacing: "0.25%",
                            }}
                          >
                            Not quite right? Edit to make changes or add more
                            details in the chat below.
                          </Typography>
                        </Box>
                      </>
                    )}
                  </>
                );
              })()}
            </Box>

            {value?.status === "approving" && (
              <Typography sx={statusMessageSx(isDark)}>
                Generating DPM...
              </Typography>
            )}
            {value?.status === "validating_skeleton" && (
              <Typography sx={statusMessageSx(isDark)}>
                Saving changes...
              </Typography>
            )}
            {value?.status === "skeleton_saved" && (
              <Typography sx={statusMessageSx(isDark)}>
                Changes saved.
              </Typography>
            )}
          </Box>
        ) : value?.action === "DPM_GENERATED" && value?.dpm_filename ? (
          <>
            <Typography
              sx={{
                fontSize: "16px",
                lineHeight: "24px",
                color: isDark ? "#D5D9DF" : "#434950",
                fontFamily: "Inter",
                fontWeight: "400",
                letterSpacing: "0.5%",
              }}
            >
              DPM generated successfully!
            </Typography>
            {onViewDpm && (
              <Button
                onClick={() =>
                  onViewDpm(
                    value?.file_id,
                    value?.dpm_filename,
                    "view",
                    value?.dpm_uuid,
                  )
                }
                variant='contained'
                size='small'
                sx={{
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #146E37 0%, #1A8744 100%)",
                  height: "36px",
                  width: "fit-content",
                  padding: "8px 12px",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  textTransform: "none",
                  fontFamily: "Inter",
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "20px",
                  letterSpacing: "0.15%",
                  mt: "12px",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #146E37 0%, #1A8744 100%)",
                  },
                  "&.Mui-disabled": {
                    cursor: "not-allowed",
                    pointerEvents: "auto",
                    color: "#FFFFFF",
                    opacity: 0.6,
                  },
                }}
              >
                View DPM
              </Button>
            )}
          </>
        ) : value?.action === "OUTLINE_GENERATED" && value?.outline_text ? (
          <Typography sx={mdTextSx(isDark)}>{value.outline_text}</Typography>
        ) : value?.action === "FAILED" && value?.failure_reason ? (
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: "20px",
              fontFamily: "Inter",
              fontWeight: "400",
              letterSpacing: "0.25%",
              color: "#E53935",
            }}
          >
            {value.failure_reason}
          </Typography>
        ) : typeof value === "string"  ? (
          <ReactMarkdown
            remarkPlugins={[remarkBreaks]}
            components={{
              p: ({ children }) => (
                <Typography sx={mdTextSx(isDark)}>{children}</Typography>
              ),
              h1: ({ children }) => (
                <Typography
                  component='h1'
                  sx={{
                    ...mdTextSx(isDark),
                    fontSize: "20px",
                    fontWeight: "600",
                  }}
                >
                  {children}
                </Typography>
              ),
              h2: ({ children }) => (
                <Typography
                  component='h2'
                  sx={{
                    ...mdTextSx(isDark),
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  {children}
                </Typography>
              ),
              h3: ({ children }) => (
                <Typography
                  component='h3'
                  sx={{
                    ...mdTextSx(isDark),
                    fontSize: "17px",
                    fontWeight: "600",
                  }}
                >
                  {children}
                </Typography>
              ),
              ul: ({ children }) => (
                <Box
                  component='ul'
                  sx={{
                    pl: 3,
                    mb: 1,
                    fontFamily: "Inter",
                    listStyleType: "disc",
                  }}
                >
                  {children}
                </Box>
              ),
              ol: ({ children }) => (
                <Box
                  component='ol'
                  sx={{
                    pl: 3,
                    mb: 1,
                    fontFamily: "Inter",
                    listStyleType: "decimal",
                  }}
                >
                  {children}
                </Box>
              ),
              li: ({ children }) => (
                <Typography
                  component='li'
                  sx={{ ...mdTextSx(isDark), display: "list-item" }}
                >
                  {children}
                </Typography>
              ),
              strong: ({ children }) => (
                <Box component='strong' sx={{ fontWeight: "600" }}>
                  {children}
                </Box>
              ),
              code: ({ inline, children }) =>
                inline ? (
                  <Box
                    component='code'
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "14px",
                      background: isDark ? "#2A2D35" : "#F0F2F5",
                      color: isDark ? "#F1F4F8" : "#434950",
                      px: 0.5,
                      borderRadius: "3px",
                    }}
                  >
                    {children}
                  </Box>
                ) : (
                  <Box
                    component='pre'
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "14px",
                      background: isDark ? "#2A2D35" : "#F0F2F5",
                      color: isDark ? "#F1F4F8" : "#434950",
                      p: 1.5,
                      borderRadius: "6px",
                      overflowX: "auto",
                      mb: 1,
                    }}
                  >
                    <code>{children}</code>
                  </Box>
                ),
            }}
          >
            {value}
          </ReactMarkdown>
        ) : value && typeof value === "object" ? (
          <Typography sx={mdTextSx(isDark)}>
            {value.message ||
              value.clarification_message ||
              value.failure_reason ||
              JSON.stringify(value, null, 2)}
          </Typography>
        ) : (
          <Typography sx={mdTextSx(isDark)}>Something went wrong.</Typography>
        )}
      </Box>
    </Box>
  );
}
